import {Database} from '@tnc/shared';
import clsx from 'clsx';
import {useRef, useState} from 'react';
import {Autoplay} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import {useMarkets} from '@/providers/components/marketsProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {DataTableRpcRefT} from '@/components/dataTableRpc';

import 'swiper/css';

import BrandSelectionModal from './brandSelectionModal';
import BuyOrderCard from './buyOrderCard';
import FundCard from './fundCard';
import MarketSelectionModal from './marketSelectionModal';
import ProductSelectionModal from './productSelectionModal';
import SellOrderCard from './sellOrderCard';
import WithdrawCard from './withdrawCard';

export interface BuySellTransferListProps {
  className?: string;
  onPositionFilled?: () => void;
}

function BuySellTransferList({className, onPositionFilled}: BuySellTransferListProps) {
  // Hooks
  const {getDistributorIdsForMarket} = useMarkets();
  const {toast} = useNotifications();

  // State
  const [activeTab, setActiveTab] = useState('buy');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [swiper, setSwiper] = useState<any>(null);
  const [productModalState, setProductModalState] = useState<{
    marketId: number;
    brandId?: number;
    type: 'buy' | 'sell' | 'transfer';
  } | null>(null);
  const [isBrandSearchOpen, setIsBrandSearchOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<{market_id: number; market_name: string} | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<
    Database['public']['Functions']['get_brands_positions_aggregates']['Returns'][0] | null
  >(null);
  const [distributorIds, setDistributorIds] = useState<number[]>([]);

  // Refs
  const productTableRef = useRef<DataTableRpcRefT>(null);
  const marketTableRef = useRef<DataTableRpcRefT>(null);
  const brandTableRef = useRef<DataTableRpcRefT>(null);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    if (swiper) {
      switch (tab) {
        case 'buy':
          swiper.slideTo(0);
          break;
        case 'sell':
          swiper.slideTo(1);
          break;
        case 'withdraw':
          swiper.slideTo(3);
          break;
        case 'deposit':
          swiper.slideTo(2);
          break;
      }
    }
  };

  const getSlideIndex = () => {
    switch (activeTab) {
      case 'buy':
        return 0;
      case 'sell':
        return 1;
      case 'withdraw':
        return 3;
      case 'deposit':
        return 2;
      default:
        return 0;
    }
  };

  // Opens the product selection modal with the market and brand ids
  const initiateProductSelection = ({
    marketId,
    brandId,
    type,
  }: {
    marketId: number | undefined;
    brandId?: number;
    type: 'buy' | 'sell' | 'transfer';
  }) => {
    if (!marketId) {
      toast.error('Please select a market');
      return;
    }

    setProductModalState({marketId, brandId, type});
  };

  // Handle market selection and fetch distributor IDs
  const handleMarketSelect = async (market: {market_id: number; market_name: string}) => {
    setSelectedMarket(market);
    setSelectedBrand(null); // Reset brand when market changes

    if (market) {
      const ids = await getDistributorIdsForMarket(market.market_id);
      setDistributorIds(ids || []);
    } else {
      setDistributorIds([]);
    }
  };

  return (
    <>
      <div className={clsx('grid grid-cols-12 gap-x-6', className)}>
        <div className="col-span-12">
          <div className="mb-4 flex w-full items-center justify-between">
            <div className="flex w-full items-center gap-2">
              <h6 className="mb-0 text-[1rem] font-semibold text-defaulttextcolor dark:text-defaulttextcolor/70">
                Quick Order Actions:
              </h6>
              <button
                type="button"
                className={clsx(
                  'ti-btn ti-btn-success ti-btn-wave waves-effect waves-light !px-3 !py-2 !text-[0.75rem]',
                  activeTab === 'buy' ? 'bg-success text-white' : ''
                )}
                onClick={() => handleTabClick('buy')}
              >
                Buy
              </button>
              <button
                className={clsx(
                  'ti-btn ti-btn-danger ti-btn-wave waves-effect waves-light !px-3 !py-2 !text-[0.75rem]',
                  activeTab === 'sell' ? 'bg-danger text-white' : ''
                )}
                onClick={() => handleTabClick('sell')}
              >
                Sell
              </button>
              <button
                type="button"
                className={clsx(
                  'ti-btn ti-btn-info ti-btn-wave waves-effect waves-light !px-3 !py-2 !text-[0.75rem]',
                  activeTab === 'deposit' ? 'bg-info text-white' : ''
                )}
                onClick={() => handleTabClick('deposit')}
              >
                Deposit
              </button>
              <button
                type="button"
                className={clsx(
                  'ti-btn ti-btn-primary ti-btn-wave waves-effect waves-light !px-3 !py-2 !text-[0.75rem]',
                  activeTab === 'withdraw' ? 'bg-primary text-white' : ''
                )}
                onClick={() => handleTabClick('withdraw')}
              >
                withdraw
              </button>
            </div>
          </div>
          <div className="swiper pagination-dynamic text-start" id="nft-collections-slide">
            <div className="swiper-wrapper">
              <Swiper
                onSwiper={setSwiper}
                spaceBetween={30}
                centeredSlides={true}
                // autoplay={!userSelected ? {delay: 2500, disableOnInteraction: true} : false}
                autoplay={false}
                modules={[Autoplay]}
                slidesPerView={1}
                onActiveIndexChange={index =>
                  setActiveTab(
                    index.activeIndex === 0
                      ? 'buy'
                      : index.activeIndex === 1
                        ? 'sell'
                        : index.activeIndex === 2
                          ? 'deposit'
                          : 'withdraw'
                  )
                }
                preventClicks={false}
                simulateTouch={false}
                initialSlide={getSlideIndex()}
                className="mySwiper swiper swiper-basic"
              >
                <SwiperSlide>
                  <BuyOrderCard
                    onOpenProductSelection={(marketId: number, brandId: number) => {
                      productTableRef.current?.refetch();
                      initiateProductSelection({marketId, brandId, type: 'buy'});
                    }}
                    onOpenMarketSearch={() => {
                      marketTableRef.current?.refetch();
                    }}
                    onOpenBrandSearch={() => {
                      setIsBrandSearchOpen(true);
                      brandTableRef.current?.refetch();
                    }}
                    selectedMarket={selectedMarket}
                    selectedBrand={selectedBrand}
                    onPositionFilled={onPositionFilled}
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <SellOrderCard
                    onOpenProductSelection={(marketId: number | undefined) => {
                      initiateProductSelection({marketId, type: 'sell'});
                      productTableRef.current?.refetch();
                    }}
                    onOpenMarketSearch={() => {
                      marketTableRef.current?.refetch();
                    }}
                    selectedMarket={selectedMarket}
                    onPositionFilled={onPositionFilled}
                  />
                </SwiperSlide>

                <SwiperSlide>
                  <FundCard />
                </SwiperSlide>
                <SwiperSlide>
                  <WithdrawCard />
                </SwiperSlide>
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      {/* Product Selection Modal for Buy Orders */}
      <ProductSelectionModal
        productRef={productTableRef}
        marketId={productModalState?.marketId || 0}
        brandId={productModalState?.brandId || 0}
        type={productModalState?.type}
      />

      <MarketSelectionModal
        onSelect={handleMarketSelect}
        type={activeTab === 'sell' ? 'sell' : 'buy'}
        marketRef={marketTableRef}
      />

      <BrandSelectionModal
        isVisible={isBrandSearchOpen}
        onSelect={brand => setSelectedBrand(brand)}
        distributorIds={distributorIds}
        brandRef={brandTableRef}
      />
    </>
  );
}

export default BuySellTransferList;
